import {
  CLOUD_BUMP,
  cloudOutline,
  cloudPath,
  cloudProps,
  type CloudShape,
} from "sketch/shapes/cloud";
import {
  BaseBoxShapeTool,
  BaseBoxShapeUtil,
  Polygon2d,
  SVGContainer,
  getColorValue,
  useValue,
  Vec,
  type Editor,
  type SvgExportContext,
  type TLIndicatorPath,
  type TLUiIconJsx,
  type TLUiOverrides,
  type TLUiToolItem,
} from "tldraw";

/** tldraw's stroke widths by size; sketch.css holds them constant on screen. */
const STROKE = { s: 2, m: 3.5, l: 5, xl: 10 } as const;

function ink(editor: Editor, shape: CloudShape, mode: "dark" | "light") {
  return getColorValue(
    editor.getCurrentTheme().colors[mode],
    shape.props.color,
    "solid",
  );
}

function cloudSvg(shape: CloudShape, stroke: string) {
  const { w, h, size } = shape.props;
  return (
    <path
      d={cloudPath(w, h, CLOUD_BUMP[size])}
      fill="none"
      stroke={stroke}
      strokeWidth={STROKE[size]}
      strokeLinejoin="miter"
    />
  );
}

export class CloudShapeUtil extends BaseBoxShapeUtil<CloudShape> {
  static override type = "cloud" as const;
  static override props = cloudProps;

  override getDefaultProps(): CloudShape["props"] {
    return { w: 1, h: 1, color: "red", size: "m" };
  }

  override component(shape: CloudShape) {
    const stroke = useValue(
      "cloud ink",
      () => ink(this.editor, shape, this.editor.getColorMode()),
      [this.editor, shape],
    );
    return <SVGContainer>{cloudSvg(shape, stroke)}</SVGContainer>;
  }

  override toSvg(shape: CloudShape, ctx: SvgExportContext) {
    return cloudSvg(shape, ink(this.editor, shape, ctx.colorMode));
  }

  /** The outline as drawn, scallops and all, and hollow: a click on a crown
   *  finds the cloud, and a click inside reaches the marks it circles. */
  override getGeometry(shape: CloudShape) {
    const { w, h, size } = shape.props;
    return new Polygon2d({
      points: cloudOutline(w, h, CLOUD_BUMP[size]).map(
        (p) => new Vec(p.x, p.y),
      ),
      isFilled: false,
    });
  }

  override getIndicatorPath(shape: CloudShape): TLIndicatorPath {
    const path = new Path2D();
    path.rect(0, 0, shape.props.w, shape.props.h);
    return path;
  }
}

/** Drag out a cloud like a rectangle. */
export class CloudTool extends BaseBoxShapeTool {
  static override id = "cloud";
  override shapeType = "cloud" as const;
}

// Not `tlui-icon`: that class masks a background color, blanking JSX icons.
const CloudIcon: TLUiIconJsx = (
  <div style={{ width: 18, height: 18 }}>
    <svg viewBox="0 0 18 18" width="100%" height="100%" fill="none">
      <path
        d={cloudPath(14, 10, 3.5)}
        transform="translate(2 4)"
        stroke="currentColor"
        strokeWidth="1.4"
      />
    </svg>
  </div>
);

export const cloudToolOverride: TLUiOverrides = {
  translations: { en: { "tool.cloud": "Revision cloud" } },
  tools(editor, tools) {
    tools.cloud = {
      id: CloudTool.id,
      label: "tool.cloud",
      icon: CloudIcon,
      kbd: "c",
      onSelect() {
        editor.setCurrentTool(CloudTool.id);
      },
    } satisfies TLUiToolItem;
    return tools;
  },
};
